import axios from "axios";
import { API, DEFAULT_API_BASE_URL } from "../config";

export const AUTH_TOKEN_KEY = "token";
export const REFRESH_TOKEN_KEY = "refreshToken";

const baseURL = API || DEFAULT_API_BASE_URL;

const instance = axios.create({
  baseURL,
  timeout: 20000,
});

const isAuthUrl = (url) => /auth\/(login|refresh|register|otp)/.test(String(url || ""));

// Reads the `exp` claim (ms) from a JWT without verifying it. Returns null if it can't be read.
const getTokenExpiryMs = (token) => {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = decodeURIComponent(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const exp = JSON.parse(json).exp;
    return typeof exp === "number" ? exp * 1000 : null;
  } catch {
    return null;
  }
};

const isTokenExpiring = (token, skewMs = 30000) => {
  const expiry = getTokenExpiryMs(token);
  return expiry !== null && expiry - skewMs <= Date.now();
};

instance.interceptors.request.use(async (config) => {
  let token = localStorage.getItem(AUTH_TOKEN_KEY);

  // Refresh BEFORE sending when the access token is already (about to be) expired.
  // Some backends answer an expired JWT with 403 instead of 401, so reacting only
  // to 401 responses would never trigger a refresh.
  if (
    token &&
    !isAuthUrl(config.url) &&
    localStorage.getItem(REFRESH_TOKEN_KEY) &&
    isTokenExpiring(token)
  ) {
    try {
      token = await refreshAccessToken();
    } catch {
      // fall through: the response interceptor will log the user out if the server rejects it
    }
  }

  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }

  config.headers["x-country"] = localStorage.getItem("muradi_country") || "PK";
  config.headers["x-language"] = localStorage.getItem("muradi_language") || "en";

  return config;
});

function getFriendlyMessage(error) {
  if (error && error.code === "ECONNABORTED") {
    return "This is taking longer than expected. Please try again.";
  }
  if (!error || !error.response) {
    return "Couldn't connect to the server. Please check your internet connection.";
  }
  const status = error.response.status;
  const url = String(error.config?.url || "");
  const isLoginRequest = url.includes("auth/login");

  if (isLoginRequest && (status === 401 || status === 403 || status === 400)) {
    return (
      (error.response.data && error.response.data.message) ||
      "Incorrect email/phone or password. Please try again."
    );
  }
  if (status === 401 || status === 403) {
    return "Your session has expired. Please log in again.";
  }
  if (status === 404) {

    const method = (error.config?.method || "?").toUpperCase();
    const url = `${error.config?.baseURL || ""}${error.config?.url || ""}`;
    return `This action isn't available right now (${method} ${url} returned 404 - endpoint not found on the server). Please share this with your backend/developer.`;
  }
  if (status >= 500) {
    return "Something went wrong on the server. Please try again in a moment.";
  }
  return (error.response.data && error.response.data.message) || "Something went wrong. Please try again.";
}

let refreshPromise = null;

// All concurrent callers share one in-flight refresh request.
function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

const clearSessionAndRedirect = () => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem("user");
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

const doRefresh = async () => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  const res = await axios.post(
    `${baseURL}/auth/refresh`,
    { refreshToken },
    {
      headers: {
        "x-country": localStorage.getItem("muradi_country") || "PK",
        "x-language": localStorage.getItem("muradi_language") || "en",
      },
    }
  );
  const body = (res.data && res.data.data) || res.data || {};
  const newAccessToken = body.accessToken;
  const newRefreshToken = body.refreshToken;

  if (!newAccessToken) {
    throw new Error("Refresh response did not include an accessToken");
  }

  localStorage.setItem(AUTH_TOKEN_KEY, newAccessToken);
  if (newRefreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
  }

  try {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      const merged = {
        ...parsed,
        name: body.name || parsed.name,
        email: body.email || parsed.email,
        country: body.country || parsed.country,
        roleName: body.role || parsed.roleName,
      };
      localStorage.setItem("user", JSON.stringify(merged));
    }
  } catch {

  }

  return newAccessToken;
};

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error && error.response && error.response.status;
    const authUrl = isAuthUrl(originalRequest.url);
    const hadToken = !!localStorage.getItem(AUTH_TOKEN_KEY);

    // 401 => token rejected. 403 => only treat as expiry when the JWT really is expired,
    // otherwise it is a genuine "no permission" answer and must not log the user out.
    const looksExpired =
      status === 401 ||
      (status === 403 && hadToken && isTokenExpiring(localStorage.getItem(AUTH_TOKEN_KEY), 0));

    if (looksExpired && !authUrl && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        return instance(originalRequest);
      } catch (refreshErr) {
        clearSessionAndRedirect();
        error.friendlyMessage = getFriendlyMessage(error);
        return Promise.reject(error);
      }
    }

    error.friendlyMessage = getFriendlyMessage(error);

    if (status === 401 && !authUrl) {
      clearSessionAndRedirect();
    }
    return Promise.reject(error);
  }
);

export default instance;
